import {
	Autocomplete,
	Box,
	LinearProgress,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/system";
import useStyles from "./styles";
import GraficosResumo from "./charts";
import { useEffect, useState } from "react";
import { set } from "react-hook-form";

export default function StatusCliente({
	networkSummary = {},
	loadingCharts = {},
	selectedRow = {},
	selectedMonth = "actual",
	loadingInterface = false,
	allInterfaces = [],
	selectedInterface = null,
	setSelectedInterface = () => {},
	errorPacketCount = {},
	statusInterface = null,
	height = "500px",
}) {
	// Estilização

	const theme = useTheme();
	const styles = useStyles(theme);

	const [windowWith, setWindowWith] = useState(window.innerWidth);

	const allSelected =
		Array.isArray(selectedInterface) &&
		allInterfaces.length > 0 &&
		selectedInterface.length === allInterfaces.length;

	const handleSelectAll = () => {
		if (allSelected) {
			setSelectedInterface([]);
		} else {
			setSelectedInterface(allInterfaces?.map((i) => i?.name));
		}
	};

	const handleChange = (e, value) => {
		setSelectedInterface(value);
	};

	useEffect(() => {
		const handleResize = () => {
			setWindowWith(window.innerWidth);
		};

		window.addEventListener("resize", handleResize);
	}, []);

	return (
		<Paper
			sx={{ ...styles.container, height }}
			elevation={theme.palette.mode == "dark" ? 0 : 3}
		>
			<Box sx={styles.boxHeader}>
				<Autocomplete
					multiple
					disableCloseOnSelect
					disabled={loadingInterface || !selectedRow}
					id="multi-select-interface"
					size="small"
					value={Array.isArray(selectedInterface) ? selectedInterface : []}
					onChange={(e, newValue, reason) => {
						const isSelectAll = newValue.some(
							(val) =>
								val === "__select_all__" || val?.name === "__select_all__",
						);

						if (isSelectAll) {
							if (allSelected) {
								setSelectedInterface([]);
							} else {
								setSelectedInterface(allInterfaces?.map((i) => i?.name));
							}
						} else {
							const onlyStrings = newValue.map((item) =>
								typeof item === "string" ? item : item?.name,
							);
							handleChange(e, onlyStrings);
						}
					}}
					sx={{ width: 300, height: "40px" }}
					getOptionLabel={(option) => {
						if (option === "__select_all__") return "Selecionar todos";
						if (typeof option === "string") return option;
						if (option?.description)
							return `${option.name} - ${option.description}`;
						return option?.name || "";
					}}
					isOptionEqualToValue={(option, value) => {
						const optVal = typeof option === "string" ? option : option?.name;
						const valVal = typeof value === "string" ? value : value?.name;
						return optVal === valVal;
					}}
					options={[
						"__select_all__",
						...(Array.isArray(allInterfaces) ? allInterfaces : []),
					]}
					renderInput={(params) => (
						<Box sx={{ position: "relative" }}>
							<TextField
								size="small"
								sx={styles.autocompleteTextField}
								{...params}
								label="Interface"
							/>
							{loadingInterface ? (
								<LinearProgress sx={styles.linerProgress} />
							) : null}
						</Box>
					)}
					renderOption={(props, option, { selected }) => {
						if (option === "__select_all__") {
							return (
								<li {...props} key="select-all-key">
									<Box component="span" sx={{ mr: 1 }}>
										<input
											type="checkbox"
											checked={allSelected}
											indeterminate={
												selectedInterface &&
												selectedInterface.length > 0 &&
												!allSelected
											}
											onChange={handleSelectAll}
											style={{ pointerEvents: "auto" }}
										/>
									</Box>
									Selecionar todos
								</li>
							);
						}
						return (
							<li
								{...props}
								key={typeof option === "string" ? option : option?.name}
							>
								<Box component="span" sx={{ mr: 1 }}>
									<input
										type="checkbox"
										checked={selected}
										readOnly
										style={{ pointerEvents: "none" }}
									/>
								</Box>
								{typeof option === "string"
									? option
									: option?.description
										? `${option.name} - ${option.description}`
										: option?.name}
							</li>
						);
					}}
					renderTags={(selected) =>
						selected.length === 1
							? [
									typeof selected[0] === "string"
										? selected[0]
										: selected[0]?.name,
								]
							: windowWith > 500
								? [`${selected.length} interfaces selecionadas`]
								: [`${selected.length} selecionadas`]
					}
				/>

				{statusInterface && (
					<Box sx={styles.boxStatusInterfaces}>
						<Tooltip
							title={
								statusInterface?.up.length > 0 && (
									<Box sx={styles.boxTooltip}>
										{statusInterface?.up?.map((iFace, index) => {
											return (
												<Typography key={`up-${index}`} sx={styles.textTooltip}>
													{Object.keys(iFace)[0]?.match(/\[(.*?)\]/)?.[1]}
												</Typography>
											);
										})}
									</Box>
								)
							}
							componentsProps={{
								tooltip: {
									sx: {
										bgcolor: theme.palette.paletteCharts.lineCharts,
										margin: 0,
									},
								},
							}}
							arrow
						>
							<Box sx={styles.boxStatusText}>
								<Box
									sx={{
										bgcolor:
											theme.palette.mode === "dark"
												? theme.palette.cardTrafficData.iconColor.statusUp
												: theme.palette.cardTrafficData.paperColor.statusUp,
										width: "8px",
										height: "8px",
										borderRadius: "50%",
										mr: 1,
										...(statusInterface?.up?.length > 0 && {
											animation: "pulse 1.5s infinite",
											boxShadow: `0 0 10px ${
												theme.palette.mode === "dark"
													? theme.palette.cardTrafficData.iconColor.statusUp
													: theme.palette.cardTrafficData.paperColor.statusUp
											}`,
										}),
										"@keyframes pulse": {
											"0%": {
												transform: "scale(1)",
												opacity: 1,
											},
											"50%": {
												transform: "scale(1.2)",
												opacity: 0.5,
											},
											"100%": {
												transform: "scale(1)",
												opacity: 1,
											},
										},
									}}
								/>
								<Typography sx={styles.textTitle}>Up:</Typography>
								<Typography
									sx={{
										...styles.textStatus,
										color:
											theme.palette.mode === "dark"
												? theme.palette.cardTrafficData.iconColor.statusUp
												: theme.palette.cardTrafficData.paperColor.statusUp,
									}}
								>
									{statusInterface?.up?.length}
								</Typography>
							</Box>
						</Tooltip>

						<Tooltip
							title={
								statusInterface?.down.length > 0 && (
									<Box sx={styles.boxTooltip}>
										{statusInterface?.down?.map((iFace, index) => {
											return (
												<Typography
													key={`down-${index}`}
													sx={styles.textTooltip}
												>
													{Object.keys(iFace)[0]?.match(/\[(.*?)\]/)?.[1]}
												</Typography>
											);
										})}
									</Box>
								)
							}
							componentsProps={{
								tooltip: {
									sx: {
										bgcolor: theme.palette.paletteCharts.lineCharts,
										margin: 0,
									},
								},
							}}
							arrow
						>
							<Box sx={styles.boxStatusText}>
								<Box
									sx={{
										bgcolor:
											theme.palette.mode === "dark"
												? theme.palette.cardTrafficData.iconColor.statusDown
												: theme.palette.cardTrafficData.paperColor.statusDown,
										width: "8px",
										height: "8px",
										borderRadius: "50%",
										mr: 1,
										...(statusInterface?.down?.length > 0 && {
											animation: "pulse 1.5s infinite",
											boxShadow: `0 0 10px ${
												theme.palette.mode === "dark"
													? theme.palette.cardTrafficData.iconColor.statusDown
													: theme.palette.cardTrafficData.paperColor.statusDown
											}`,
										}),
										"@keyframes pulse": {
											"0%": {
												transform: "scale(1)",
												opacity: 1,
											},
											"50%": {
												transform: "scale(1.2)",
												opacity: 0.5,
											},
											"100%": {
												transform: "scale(1)",
												opacity: 1,
											},
										},
									}}
								/>
								<Typography sx={styles.textTitle}>Down:</Typography>
								<Typography
									sx={{
										...styles.textStatus,
										color:
											theme.palette.mode === "dark"
												? theme.palette.cardTrafficData.iconColor.statusDown
												: theme.palette.cardTrafficData.paperColor.statusDown,
									}}
								>
									{statusInterface?.down?.length}
								</Typography>
							</Box>
						</Tooltip>
					</Box>
				)}
			</Box>
			<GraficosResumo
				errorPacketCount={errorPacketCount}
				networkSummary={networkSummary}
				loadingCharts={loadingCharts}
				selectedRow={selectedRow}
				selectedMonth={selectedMonth}
				selectedInterface={selectedInterface}
			/>
		</Paper>
	);
}
